import { Injectable, signal, WritableSignal } from '@angular/core';
import { ApiGraphqlService } from '@core/services/api.graphql.service';
import { DataTableConfiguration } from '@app/shared/interfaces/data-table-configuration.interface';
import { DeserializableModel } from '@shared/models/deserializable.model';
import { Observable, of } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { DataTableVariables } from '@app/shared/interfaces/data-table-variables.interface';
import { DataTableState } from '@shared/interfaces/data-table-state.interface';

@Injectable({
  providedIn: 'root',
})
export class DataTableService {
  // Dictionary to store signals for each table based on config.id
  private readonly rowSelectionSignals: Record<
    string,
    WritableSignal<DeserializableModel | null>
  > = {};
  private readonly multiRowSelectionSignals: Record<
    string,
    WritableSignal<DeserializableModel[]>
  > = {};
  private readonly reloadSignal: WritableSignal<DataTableConfiguration | null> =
    signal(null);
  private readonly deleteNotificationSignal: WritableSignal<string | null> =
    signal(null);
  constructor(private apiGraphqlService: ApiGraphqlService) {}

  getReloadSignal(): WritableSignal<DataTableConfiguration | null> {
    return this.reloadSignal;
  }

  getDeleteNotificationSignal(): WritableSignal<string | null> {
    return this.deleteNotificationSignal;
  }

  notifyItemDeleted(tableId: string): void {
    this.deleteNotificationSignal.set(tableId);
  }

  getTableStateKey(config: DataTableConfiguration): string {
    return `datatable_state_${config.id}`;
  }

  getTableState(
    config: DataTableConfiguration | undefined,
  ): DataTableState | null {
    if (!config) return null;
    if (config.stateless) return null;
    const key = this.getTableStateKey(config);
    const stateJson = localStorage.getItem(key);
    return stateJson ? JSON.parse(stateJson) : null;
  }

  saveTableState(config: DataTableConfiguration, state: DataTableState): void {
    if (config.stateless) return;
    const key = this.getTableStateKey(config);
    localStorage.setItem(key, JSON.stringify(state));
  }

  cleanAllTableStates(): void {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('datatable_state_')) {
        localStorage.removeItem(key);
      }
    });
    // NOTE: Clear all row selection signals as well
    Object.keys(this.rowSelectionSignals).forEach((key) => {
      this.rowSelectionSignals[key].set(null);
    });
    Object.keys(this.multiRowSelectionSignals).forEach((key) => {
      this.multiRowSelectionSignals[key].set([]);
    });
  }

  // Multi-row selection methods
  getMultiRowSelectionSignal(
    config: DataTableConfiguration,
  ): WritableSignal<DeserializableModel[]> {
    if (!this.multiRowSelectionSignals[config.id]) {
      this.multiRowSelectionSignals[config.id] = signal<DeserializableModel[]>(
        [],
      );
    }
    return this.multiRowSelectionSignals[config.id];
  }

  getSelectedRows(config: DataTableConfiguration): DeserializableModel[] {
    return this.getMultiRowSelectionSignal(config)();
  }

  toggleRowSelection(
    config: DataTableConfiguration,
    row: DeserializableModel,
  ): void {
    const selectedRows = this.getSelectedRows(config);
    const index = selectedRows.findIndex((r) => r.id === row.id);

    if (index > -1) {
      // Remove row from selection
      const newSelection = [...selectedRows];
      newSelection.splice(index, 1);
      this.getMultiRowSelectionSignal(config).set(newSelection);
    } else {
      // Add row to selection
      this.getMultiRowSelectionSignal(config).set([...selectedRows, row]);
    }
  }

  clearMultiRowSelection(config: DataTableConfiguration): void {
    this.getMultiRowSelectionSignal(config).set([]);
  }

  isRowSelected(
    config: DataTableConfiguration,
    row: DeserializableModel,
  ): boolean {
    const selectedRows = this.getSelectedRows(config);
    return selectedRows.some((r) => r.id === row.id);
  }

  selectRow(
    config: DataTableConfiguration | undefined,
    row: DeserializableModel | null,
    reloadDataTable = false,
  ): void {
    if (!config) return;
    this.getRowSelectionSignal(config).set(row);
    if (reloadDataTable) {
      this.reloadDataTable(config);
    }
  }

  getSelectedRow(config: DataTableConfiguration): DeserializableModel | null {
    return this.getRowSelectionSignal(config)();
  }

  reloadDataTable(
    config: DataTableConfiguration,
    clearRowSelection = false,
  ): void {
    if (clearRowSelection) {
      this.clearRowSelection(config, true);
    }
    this.reloadSignal.set(config);
  }

  clearRowSelection(config: DataTableConfiguration, updateState = false): void {
    this.getRowSelectionSignal(config).set(null);
    if (updateState) {
      const state = this.getTableState(config);
      if (state) {
        state.selectedRow = null;
        this.saveTableState(config, state);
      }
    }
  }

  getRowSelectionSignal(
    config: DataTableConfiguration,
  ): WritableSignal<DeserializableModel | null> {
    if (!this.rowSelectionSignals[config.id]) {
      this.rowSelectionSignals[config.id] = signal<DeserializableModel | null>(
        null,
      );
    }
    return this.rowSelectionSignals[config.id];
  }

  private constructQuery(
    config: DataTableConfiguration,
    isPaginated: boolean,
    queryName: string,
  ): string {
    // Process columns to build GraphQL fields and nested objects
    // Filter out columns with dynamicColumnComponent as they don't represent actual data fields
    const fields = config.columns
      .filter((col) => !col.dynamicColumnComponent)
      .map((col) => {
        if (typeof col.field === 'string') {
          return col.field; // Simple string field
        } else if (col.field) {
          const { parent, children } = col.field;
          return `${parent} { ${children.join(' ')} }`; // Handle nested fields
        }
        return null;
      })
      .filter((field) => field !== null)
      .join('\n');

    return isPaginated
      ? `
        query Get${config.entityName}($take: Int, $skip: Int, $search: String${config.filters ? ',  $filters: PurchaseFilterInput' : ''}) {
          ${queryName}(take: $take, skip: $skip, search: $search${config.filters ? ', filters: $filters' : ''}) {
            items {
              ${fields}
            }
            total
          }
        }
      `
      : `
        query Get${config.entityName}($search: String) {
          ${queryName}(search: $search) {
            ${fields}
          }
        }
      `;
  }

  loadData<T extends DeserializableModel>(
    config: DataTableConfiguration | undefined,
    page = 1,
    pageSize = -1,
    searchQuery = '',
  ): { data$: Observable<T[]>; total$: Observable<number> } {
    if (!config) return { data$: of([]), total$: of(0) };
    const isPaginated = pageSize !== -1;
    const queryName = `${config.entityName.charAt(0).toLowerCase() + config.entityName.slice(1)}${isPaginated ? 'Paginated' : ''}`;

    // Dynamically construct the query string based on isPaginated
    const query = this.constructQuery(config, isPaginated, queryName);

    // Define variables conditionally based on whether pagination is used
    const variables: DataTableVariables = {
      take: isPaginated ? pageSize : undefined, // Avoid sending `null` when not paginated
      skip: (page - 1) * pageSize,
      search: config.enableSearch ? searchQuery : undefined, // Add searchQuery to variables only if enableSearch is true
      filters: config.filters,
    };

    const { data$, total$ } = this.fetchData<T>(
      config,
      query,
      variables,
      queryName,
      isPaginated,
    );
    return { data$, total$ };
  }

  private fetchData<T extends DeserializableModel>(
    config: DataTableConfiguration,
    query: string,
    variables: DataTableVariables,
    queryName: string,
    isPaginated: boolean,
  ): { data$: Observable<T[]>; total$: Observable<number> } {
    if (isPaginated) {
      const apiQuery$ = this.apiGraphqlService
        .queryPaginated<T>(
          config.modelConstructor as new () => T,
          query,
          variables,
          queryName,
        )
        .pipe(share());
      const data$: Observable<T[]> = apiQuery$.pipe(
        map((result) => result.items),
      );
      const total$: Observable<number> = apiQuery$.pipe(
        map((result) => result.total),
      );
      return { data$, total$ };
    } else {
      const data$ = this.apiGraphqlService.query<T>(
        config.modelConstructor as new () => T,
        query,
        variables as Record<string, unknown>,
        queryName,
      );
      const total$ = data$.pipe(map((items) => items.length));
      return { data$, total$ };
    }
  }
}
